import React, { useEffect, useState, useRef, useMemo } from "react";
import styles from "./styles.module.scss";
import BarChart from "./BarChart";
import GroupedBarsChart from "./GroupedBarsChart";
import PieChart from "./PieChart";
import LineChart from "./LineChart";
import StackedBarsChart from "./StackedBarsChart";
import HeatmapChart from "./HeatmapChart";
import MosaicChart from "./MosaicChart";
import MultipleLineChart from "./MultipleLineChart";
import {API_URL} from "../../utils/Shared/constant";
import Axios from "axios";
import CheckerGroup from "./CheckerGroup";
import { ALL_CHART_TYPES } from "../CustomDataInputPage/DATA_TYPES";
import { Link } from "react-router-dom";
import DataImageGenerator from "./DataImageGenerator";
import ChartInfo from "./ChartInfo";
import html2canvas from "html2canvas";
import STATISTIC_TYPES, {
    FIND_STATISTIC_TYPE_BY_VALUE,
    ALL_STATISTICS_TYPES,
} from "./STATISTIC_TYPES";
import DATA_TYPES from "../CustomDataInputPage/DATA_TYPES";

/**
 * @typedef {Object} ResponseData
 * @property {string} title - Main chart title
 * @property {string} [title2] - Secondary title (optional)
 * @property {string} [title3] - Tertiary title (optional)
 * @property {number} missing - Count of missing responses
 * @property {number} numbern - Total number of responses
 * @property {number} unknown - Count of unknown responses
 * @property {number} refused - Count of refused responses
 * @property {Array<CategoryData>} values - Response data by category
 * @property {Array<ChartInfo>} infos - Chart metadata
 */

/**
 * @typedef {Object} CategoryData
 * @property {string|null} name - Category name
 * @property {Array<ResponseOption>} data - Response options and values
 */

/**
 * @typedef {Object} ResponseOption
 * @property {string} name - Response option
 * @property {string} value - Response percentage as decimal string
 */

/**
 * @typedef {Object} ChartInfo
 * @property {string} title - Chart title
 * @property {string|null} remark - Additional notes
 */

const WEIGHTED_TYPES = [
    {
        value: 1,
        label: "全部震度",
    },
    {
        value: 2,
        label: "震度四級地區",
    },
    {
        value: 3,
        label: "震度五弱以上地區",
    },
];

// 添加 debounce 函數
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

function CustomDataGenerationPage({ chartSpec, onBackBtnClicked }) {
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const [chartImageReady, setChartImageReady] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const chartImageRef = useRef(null);

    // 使用 useRef 來保存 debounced 函數的引用
    const debouncedHandleResize = useRef(
        debounce(() => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        }, 250) // 250ms 的延遲
    ).current;

    // 修改 resize 事件監聽器
    useEffect(() => {
        window.addEventListener('resize', debouncedHandleResize);
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
            // 清理 debounce timeout
            debouncedHandleResize.clear && debouncedHandleResize.clear();
        };
    }, [debouncedHandleResize]);

    const chartTitle = useMemo(() => {
        if (!data) {
            return "";
        }
        if (!data.title2 && !data.title3) {
            return `<span>${data.title}</span>`;
        }
        if (!data.title3) {
            return `<span>${data.title}</span> <small>依 ${data.title2} 分類</small>`;
        }
        return `<span>${data.title}</span> <small>依 ${data.title2} 及 ${data.title3} 分類</small>`;
    }, [data]);

    const getChartNote = useMemo(() => {
        if (!data || !chartData) return null;

        // 堆疊長條圖需要顯示 title2
        if (chartData.chartType === ALL_CHART_TYPES.STACK_BAR) {
            // 三層圖表才需要顯示註解
            if (data.title3) {
                return `X軸為 ${data.title2} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\nY軸為 ${data.title3} 的選項\n將游標移到圖表上，可查看詳細數據說明。`;
            }
            return `X軸為 ${data.title2} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\nY軸為 ${data.title} 的選項\n將游標移到圖表上，可查看詳細數據說明。`;
        }

        // 熱力圖需要顯示 title2 和 title3
        if (chartData.chartType === ALL_CHART_TYPES.HEATMAP) {
            if (data.title3) {
                return `X軸為 ${data.title3} 的選項\nY軸為 ${data.title2} 的選項\n顏色深度為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\n將游標移到圖表上，可查看詳細數據說明。`;
            }
            return `X軸為 ${data.title2} 的選項\nY軸為 ${data.title} 的選項\n顏色深度為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\n將游標移到圖表上，可查看詳細數據說明。`;
        }

        // 馬賽克圖需要顯示 title2
        if (chartData.chartType === ALL_CHART_TYPES.MOSAIC) {
            return `X軸為${data.title}的選項\nY軸為${data.title2}的${
                FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label
            }\n將游標移到圖表上，可查看詳細數據說明。\n馬賽克圖可能無法完整呈現所有選項，請使用者自行判斷適用性`;
        }

        //長條圖
        if (chartData.chartType === ALL_CHART_TYPES.BAR) {
            if (data.title2) {
                return `X軸為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\nY軸為 ${data.title2} 的選項`;
            }
            return `X軸為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}\nY軸為 ${data.title} 的選項`;
        }

        //分組長條圖
        if (chartData.chartType === ALL_CHART_TYPES.GROUPED_BAR) {
            return `X軸為 ${data.title} 的 ${
                FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label
            }\nY軸為 ${data.title2} 的選項`;
        }



        if (chartData.chartType === ALL_CHART_TYPES.LINE) {
            // 如果只有一層
            if (!data.title2 && !data.title3) {
                return `X軸為 ${data.title} 的選項\nY軸為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}`;
            }
            return `X軸為 ${data.title2} 的選項\nY軸為 ${data.title} 的 ${FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label}`;
        }

        // 圓餅圖和單層圖表不需要註解
        return null;
    }, [data, chartData]);


    useEffect(() => {
        if (!chartSpec || !chartSpec.step1Question) {

            return;
        }

        const title1ID = chartSpec.step1Question.Qid;
        const title2ID =
            chartSpec.step2Question && chartSpec.step2Question.Qid
                ? chartSpec.step2Question.Qid
                : null;
        const title3ID =
            chartSpec.step3Question && chartSpec.step3Question.Qid
                ? chartSpec.step3Question.Qid
                : null;
        const chartType = chartSpec.chartType;

        let statisticsType = 1;
        let finalOptions = [];

        // If step1Question is 數值型單數, use special statistics options
        if (chartSpec.step1Question.AnswerTypeName === DATA_TYPES.數值型單數) {
            // 設定共同的選項
            finalOptions = [
                STATISTIC_TYPES.平均數,
                STATISTIC_TYPES.中位數,
                STATISTIC_TYPES.實際數,
            ];

            // 根據條件設定統計類型
            if (chartSpec.step3Question || 
                (chartSpec.step2Question.AnswerTypeName === DATA_TYPES.數值型級距 && !chartSpec.step3Question)) {
                statisticsType = STATISTIC_TYPES.平均數.value;
            } else {
                statisticsType = STATISTIC_TYPES.實際數.value;
            }
        } else if (chartSpec.step1Question.AnswerTypeName === DATA_TYPES.數值型級距) {
           statisticsType = STATISTIC_TYPES.百分比.value;
           finalOptions = [STATISTIC_TYPES.百分比, STATISTIC_TYPES.實際數];
        } else {
            finalOptions = [STATISTIC_TYPES.百分比, STATISTIC_TYPES.實際數];
        }

        if (
            chartType == ALL_CHART_TYPES.LINE &&
            chartSpec.step1Question.AnswerTypeName !== DATA_TYPES.數值型級距
        ) {
            statisticsType = STATISTIC_TYPES.平均數.value;
            finalOptions = [
                STATISTIC_TYPES.平均數,
                STATISTIC_TYPES.中位數,
                STATISTIC_TYPES.實際數,
            ];
        }

        if (chartType === ALL_CHART_TYPES.BAR) {
            if (chartSpec.step1Question.AnswerTypeName === DATA_TYPES.數值型單數 && chartSpec.step2Question.AnswerTypeName !== DATA_TYPES.數值型單數) {
                statisticsType = STATISTIC_TYPES.平均數.value;
            }
        }

        setChartData({
            title1ID,
            title2ID,
            title3ID,
            chartType,
            weighted: 1,
            statisticsType,
            availableStatisticsTypesOptions: finalOptions,
        });
        setLoaded(true);
    }, [chartSpec, onBackBtnClicked]);

    const fetchData = async (url) => {
        setIsLoading(true);
        const response = await Axios.get(url);

        setData(response.data.Result);
        setIsLoading(false);
    };

    useEffect(() => {
        if (!chartData || !chartSpec.step1Question) {
            return;
        }

        let title1Part = "";
        let title2Part = "";
        let title3Part = "";
        if (chartData.title1ID) {
            title1Part = `&TitleID=${chartData.title1ID}`;
        }
        if (chartData.title2ID) {
            title2Part = `&Title2ID=${chartData.title2ID}`;
        }
        if (chartData.title3ID) {
            title3Part = `&Title3ID=${chartData.title3ID}`;
        }

        const url = `${API_URL}?${title1Part}${title2Part}${title3Part}&Weighted=${
            chartData.weighted || "1"
        }&StatisticsType=${chartData.statisticsType}`;
        fetchData(url);
    }, [chartData, chartSpec]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleDataImageLoaded = async (dom) => {
        const downloadBtn = document.querySelector(".download-btn");
        if (!downloadBtn) {
            return;
        }
        downloadBtn.disabled = true;
        try {
            // 將 DOM 轉換成 canvas
            const canvas = await html2canvas(dom, {
                scale: 2, // 提高圖片品質
                scrollY: -window.scrollY,
                onclone: (node) => {
                    // node is a html body
                    // reset node scroll
                    const nodeBody = node.querySelector("body");
                    nodeBody.style.margin = "0";
                    nodeBody.style.padding = "0";
                    nodeBody.style.overflow = "auto";

                    // reset scrollposition in nodeBody
                    nodeBody.scrollTop = 0;
                    nodeBody.scrollLeft = 0;

                    const nodeHeader = nodeBody.querySelector("header");
                    nodeHeader.remove();

                    const nodeFooter = nodeBody.querySelector("footer");
                    nodeFooter.remove();

                    const pageContainer = nodeBody.querySelector(
                        "#custom-data-generation-page"
                    );
                    pageContainer.style.padding = "0";

                    let dataImageGenerator = node.querySelector(
                        "#data-image-generator"
                    );
                    dataImageGenerator.style.opacity = 1;
                    dataImageGenerator.style.margin = "0";
                    dataImageGenerator.style.position = "relative";
                },
                width: 1080, // 提高圖片品質
                height: dom.offsetHeight, // 使用 DOM 實際高度
                backgroundColor: "#ffffff", // 確保背景是白色
                useCORS: true, // 允許跨域圖片
                logging: true, // 開啟日誌以便除錯
                imageTimeout: 0, // 不限制圖片載入時間
                removeContainer: true, // 清理暫存元素
            });

            // 將 canvas 轉換成 PNG 圖片
            const imageUrl = canvas.toDataURL("image/png");
            chartImageRef.current = imageUrl;
            setChartImageReady(true);
            downloadBtn.disabled = false;
        } catch (error) {
            console.error("轉換圖片時發生錯誤:", error);
        }
    };

    const handleDownloadPng = () => {
        if (!chartImageReady) {
            return;
        }
        let fileTitle = data.title;
        if (data.title2 && !data.title3) {
            fileTitle += `（依 ${data.title2} 分類）`;
        } else if (data.title2 && data.title3) {
            fileTitle += `（依 ${data.title2} 及 ${data.title3} 分類）`;
        }
        const link = document.createElement("a");
        link.download = `${fileTitle}.png`;
        link.href = chartImageRef.current;
        link.click();
    };

    const handleDownloadCsv = () => {
        if (!data) {
            return;
        }

        let sourceData = data.values;

        // Get all unique option names from the first category's data
        const optionNames = sourceData[0].data.map((item) => item.name);

        // Create CSV content
        let csvContent = "\ufeff"; // 添加 BOM 標記
        csvContent += `${data.title || "類別"}`; // Use data.title as header if available
        csvContent += "," + optionNames.join(",") + "\n";

        // Add data for each category
        sourceData.forEach((category) => {
            csvContent += category.name || " ";
            optionNames.forEach((optionName) => {
                const value =
                    category.data.find((item) => item.name === optionName)
                        .value || "0";
                csvContent += `,${value || " "}`;
            });
            csvContent += "\n";
        });

        // Add metadata
        csvContent += "\n";
        csvContent += "製圖的項目," + (data.title || "") + "\n";
        // csvContent += "註解," + (data.infos[0].remark || "") + "\n";
        if (data.title2) {
            csvContent += "分類的項目1," + (data.title2 || "") + "\n";
            // csvContent += "註解," + (data.infos[1].remark || "") + "\n";
        }
        if (data.title3) {
            csvContent += "分類的項目2," + (data.title3 || "") + "\n";
            // csvContent += "註解," + (data.infos[2].remark || "") + "\n";
        }
        csvContent += "註解,本調查依據母體比率進行加權後四捨五入至整數，有效樣本數為各項欄位權重數值的總和。由於四捨五入可能產生的數值誤差，如想取得調查原始數據可至首頁/資料來源申請。\n";
        csvContent += `調查地區,${
            WEIGHTED_TYPES[chartData.weighted - 1].label
        }\n`;
        csvContent += `統計方式,${
            FIND_STATISTIC_TYPE_BY_VALUE(chartData.statisticsType).label
        }\n`;
        csvContent += "\n";
        csvContent += "有效樣本," + (data.numbern || "") + "\n";
        csvContent += "為使呈現結果更好理解，上圖統計不包含以下樣本數" + "\n"

        
        // csvContent += "拒答," + (data.refused !== undefined && data.refused !== null ? data.refused : "") + "\n";

        //  // 檢查選項中是否同時包含「知道」和「不知道」
        // const hasKnowOption = data.values && data.values.some(category =>
        //     category.data && category.data.some(option => option.name === "知道")
        // );
        // const hasUnknownOption = data.values && data.values.some(category =>
        //     category.data && category.data.some(option => option.name === "不知道")
        // );
        // // 如果同時包含「知道」和「不知道」，代表這是有意義的選項，不顯示統計
        // const shouldShowUnknownStat = !(hasKnowOption && hasUnknownOption);

        // // "不知道"不是有意義的選項時才顯示這行(是否型)
        // if (shouldShowUnknownStat) {
        //     csvContent += "不知道," + (data.unknown !== undefined && data.unknown !== null ? data.unknown : "") + "\n";
        // }

        // csvContent += "不記得," + (data.forgotten !== undefined && data.forgotten !== null ? data.forgotten : "") + "\n";
        // csvContent += "遺漏值," + (data.missing !== undefined && data.missing !== null ? data.missing : "") + "\n";

         csvContent += "遺漏值(含不知道、不記得與拒答)," + data.missing + "\n";
        csvContent += "資料來源,國家災害防救科技中心減災動資料0918池上地震花東地區-地震認知與避難行為調查\n";

        let fileTitle = data.title;
        if (data.title2 && !data.title3) {
            fileTitle += `（依 ${data.title2} 分類）`;
        } else if (data.title2 && data.title3) {
            fileTitle += `（依 ${data.title2} 及 ${data.title3} 分類）`;
        }

        // Create blob with UTF-8 BOM
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${fileTitle}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <div
            id="custom-data-generation-page"
            className={styles.customDataGenerationPage}
        >
            {data && (
                <DataImageGenerator
                    data={data}
                    chartType={chartData.chartType}
                    statisticsType={chartData.statisticsType}
                    onLoaded={(dom) => {
                        handleDataImageLoaded(dom);
                    }}
                />
            )}

            <div className={styles.breadcrumbsBlock}>
                <div className={styles.breadcrumbs}>
                    {/* <Link to="/statistics/ChishangEarthquake">
                        <span>
                            0918 池上地震-花東地區地震認知與避難行為調查 (2023)
                        </span>
                    </Link>
                    <span className={styles.breadcrumbsSeparator}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                        >
                            <line
                                x1="11.6036"
                                y1="0.728553"
                                x2="0.996952"
                                y2="11.3352"
                                stroke="#5B5B5B"
                            />
                        </svg>
                    </span>
                    <span>
                        <b>客製化專區</b>
                    </span> */}
                </div>
                <div className={styles.chartDataCTA}>
                    <Link
                        to="/statistics/ChishangEarthquake/chart-data"
                        className={styles.chartDataLink}
                    >
                        前往主題圖表
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M10 8L14 12L10 16"
                                stroke="#A479E9"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </Link>
                </div>
            </div>
            <div className={styles.heroSection}>
                <div className={styles.heroSectionText}>
                    <h1>客製化專區</h1>
                    <p>Customization</p>
                </div>
            </div>
            <div className={styles.contentContainer}>
                <div className={styles.wrapper}>
                    <div className={styles.chartInfo}>
                        {data && (
                            <h2
                                dangerouslySetInnerHTML={{ __html: chartTitle }}
                            />
                        )}
                        {loaded ? (
                            <div className={styles.checkGroupsContainer}>
                                <CheckerGroup
                                    options={
                                        chartData.availableStatisticsTypesOptions
                                    }
                                    label="統計方式"
                                    name="statisticsType"
                                    value={chartData.statisticsType}
                                    onChange={(value) => {
                                        setChartData((prev) => ({
                                            ...prev,
                                            statisticsType: value,
                                        }));
                                    }}
                                />
                                <CheckerGroup
                                    options={WEIGHTED_TYPES}
                                    label="調查地區"
                                    name="weightedType"
                                    value={chartData.weighted}
                                    onChange={(value) => {
                                        setChartData((prev) => ({
                                            ...prev,
                                            weighted: value,
                                        }));
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>
                    {isLoading ? (
                        <div className={styles.loading}>Loading...</div>
                    ) : null}
                    {isLoading === false && data ? (
                        <div
                            className={styles.chartContainer}
                            key={`${windowSize.width}-${windowSize.height}`}
                        >
                            {chartData.chartType === ALL_CHART_TYPES.BAR && (
                                <BarChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType === ALL_CHART_TYPES.GROUPED_BAR && (
                                <GroupedBarsChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType === ALL_CHART_TYPES.PIE && (
                                <PieChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                />
                            )}
                            {chartData.chartType === ALL_CHART_TYPES.LINE && (
                                <LineChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType ===
                                ALL_CHART_TYPES.STACK_BAR && (
                                <StackedBarsChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType ===
                                ALL_CHART_TYPES.HEATMAP && (
                                <HeatmapChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType === ALL_CHART_TYPES.MOSAIC && (
                                <MosaicChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                            {chartData.chartType ===
                                ALL_CHART_TYPES.MULTIPLE_LINE && (
                                <MultipleLineChart
                                    data={data.values}
                                    statisticsType={chartData.statisticsType}
                                    chartTitle={data.title}
                                    chart2Title={data.title2}
                                    chart3Title={data.title3}
                                    chartNote={getChartNote}
                                />
                            )}
                        </div>
                    ) : null}
                    {data && <ChartInfo data={data} />}
                    {data && (
                        <div className={styles.actionsContainer}>
                            <button
                                onClick={onBackBtnClicked}
                                disabled={isLoading}
                            >
                                重新製作圖表
                            </button>
                            <button
                                className="download-btn"
                                disabled={!chartImageReady}
                                onClick={handleDownloadPng}
                            >
                                下載 PNG
                            </button>
                            <button 
                                onClick={handleDownloadCsv}
                                disabled={isLoading}
                            >
                                下載 CSV
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CustomDataGenerationPage;